import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { StorePerformanceItem, QuestionnaireVersion } from '../types'
import { useContextState } from '../../../../../../../hooks/theme'
import { companiesServiceFetch } from '../../../../../../../utils/constants/callService'
import { storesService } from '../../../../../../../service/stores'
import { questionarieServise } from '../../../../../../../service/questionarie'
import { httpFetch } from '../../../../../../../utils/crud'
import { isValidDate } from '../utils/periodValidation'
import { SESSION_STORAGE_KEY } from '../constants'

interface UseCompanyDataReturn {
  assignedCompany: { id: number | string; name?: string; companyName?: string } | null | undefined
  hasAssignedCompany: boolean
  initialCompany: StorePerformanceItem | null
  selectedCompany: StorePerformanceItem | null
  setSelectedCompany: (company: StorePerformanceItem | null) => void
  companiesList: StorePerformanceItem[]
  companiesLoading: boolean
  allUnits: StorePerformanceItem[]
  allQuestionnaires: StorePerformanceItem[]
  dataLoading: boolean
  showSnackbar: (message: string, type?: 'success' | 'error' | 'warning' | 'info', interpolationOptions?: Record<string, unknown>) => void
}

interface PeriodParams {
  appliedPeriodFrom: Date | null
  appliedPeriodTo: Date | null
  appliedPeriodYear: number
  appliedPeriodWeek: string[]
}

const useCompanyData = (
  showSnackbar: (message: string, type?: 'success' | 'error' | 'warning' | 'info', interpolationOptions?: Record<string, unknown>) => void,
  periodParams?: PeriodParams
): UseCompanyDataReturn => {
  const { state } = useContextState()

  const assignedCompany = state?.company
  const hasAssignedCompany = !!assignedCompany?.id

  const initialCompany: StorePerformanceItem | null = useMemo(() => {
    if (!hasAssignedCompany || !assignedCompany) return null
    return {
      id: String(assignedCompany.id),
      label: assignedCompany.name || assignedCompany.companyName || String(assignedCompany.id),
      value: String(assignedCompany.id),
    }
  }, [assignedCompany, hasAssignedCompany])

  const savedCompany = (() => {
    if (typeof window === 'undefined') return null
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null
    try {
      const saved = JSON.parse(raw)
      return saved.selectedCompany || null
    } catch {
      return null
    }
  })()

  const [selectedCompany, setSelectedCompany] = useState<StorePerformanceItem | null>(
    hasAssignedCompany ? initialCompany : (savedCompany || initialCompany)
  )
  const [companiesList, setCompaniesList] = useState<StorePerformanceItem[]>([])
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const [allUnits, setAllUnits] = useState<StorePerformanceItem[]>([])
  const [allQuestionnaires, setAllQuestionnaires] = useState<StorePerformanceItem[]>([])
  const [dataLoading, setDataLoading] = useState(false)

  const isInitialMount = useRef(true)
  const requestIdRef = useRef(0)
  const periodParamsRef = useRef(periodParams)
  useEffect(() => {
    periodParamsRef.current = periodParams
  })

  const buildPeriodQueryParams = useCallback(() => {
    const params = periodParamsRef.current
    if (!params) return {}
    const { appliedPeriodFrom, appliedPeriodTo, appliedPeriodYear, appliedPeriodWeek } = params
    return {
      date_init: isValidDate(appliedPeriodFrom) ? format(appliedPeriodFrom, 'yyyy-MM-dd') : undefined,
      date_end: isValidDate(appliedPeriodTo) ? format(appliedPeriodTo, 'yyyy-MM-dd') : undefined,
      year: appliedPeriodYear || undefined,
      weeks: appliedPeriodWeek.length > 0 ? appliedPeriodWeek.join(',') : undefined,
    }
  }, [])

  const mapStores = (rawStores: Array<{ id: number | string; name?: string; keyCode?: string | null }>): StorePerformanceItem[] =>
    rawStores.map((s) => ({
      id: String(s.id),
      label: s.name ? `${s.keyCode ?? s.id} - ${s.name}` : String(s.id),
      value: String(s.id),
    }))

  const mapQuestionnaires = (
    rawQuestionnaires: Array<{
      id: number | string
      name?: string
      version?: number
      parentId?: number | string | null
      lastVersionId?: number | string | null
      firstScheduledDate?: string | null
      lastScheduledDate?: string | null
      versions?: QuestionnaireVersion[]
    }>
  ): StorePerformanceItem[] =>
    rawQuestionnaires.map((q) => ({
      id: String(q.id),
      label: q.name || String(q.id),
      value: String(q.id),
      version: q.version,
      parentId: q.parentId != null ? String(q.parentId) : null,
      lastVersionId: q.lastVersionId != null ? String(q.lastVersionId) : null,
      firstScheduledDate: q.firstScheduledDate,
      lastScheduledDate: q.lastScheduledDate,
      versions: q.versions,
    }))

  useEffect(() => {
    if (hasAssignedCompany) return

    const fetchCompanies = async () => {
      setCompaniesLoading(true)
      try {
        const response = await companiesServiceFetch.getAllCompanies()
        const raw = Array.isArray(response) ? response : (response as { companies?: unknown[] })?.companies ?? []
        const mapped = (raw as Array<{ id: number | string; name?: string; companyName?: string }>).map((c) => ({
          id: String(c.id),
          label: c.name || c.companyName || String(c.id),
          value: String(c.id),
        }))
        setCompaniesList(mapped)
      } catch {
        setCompaniesList([])
        showSnackbar('dashboard.storePerformance.error.loadCompanies')
      } finally {
        setCompaniesLoading(false)
      }
    }

    fetchCompanies()
  }, [hasAssignedCompany, showSnackbar])

  const fetchData = useCallback(async (companyId: string) => {
    if (!companyId) return
    const currentRequestId = ++requestIdRef.current
    setDataLoading(true)
    try {
      const [storesResponse, questionnairesResponse] = await Promise.all([
        storesService(httpFetch).getStoresByCompanies(companyId),
        questionarieServise(httpFetch).getQuestionnairesWithVersions({
          company_id: companyId,
          ...buildPeriodQueryParams(),
        }),
      ])

      if (currentRequestId !== requestIdRef.current) return

      const rawStores = Array.isArray(storesResponse) ? storesResponse : []
      const rawQuestionnaires = Array.isArray(questionnairesResponse) ? questionnairesResponse : []

      setAllUnits(mapStores(rawStores))
      setAllQuestionnaires(mapQuestionnaires(rawQuestionnaires))
    } catch {
      if (currentRequestId !== requestIdRef.current) return
      setAllUnits([])
      setAllQuestionnaires([])
      showSnackbar('dashboard.storePerformance.error.loadData')
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setDataLoading(false)
      }
    }
  }, [showSnackbar, buildPeriodQueryParams])

  const loadHierarchyData = useCallback(async () => {
    const currentRequestId = ++requestIdRef.current
    setDataLoading(true)
    try {
      const companyId = String(assignedCompany?.id ?? '')
      const [storesResponse, questionnairesResponse] = await Promise.all([
        storesService(httpFetch).getStoresByHierarchy(),
        questionarieServise(httpFetch).getQuestionnairesWithVersions({
          company_id: companyId,
          ...buildPeriodQueryParams(),
        }),
      ])

      if (currentRequestId !== requestIdRef.current) return

      const rawStores = Array.isArray(storesResponse) ? storesResponse : []
      const rawQuestionnaires = Array.isArray(questionnairesResponse) ? questionnairesResponse : []

      setAllUnits(mapStores(rawStores))
      setAllQuestionnaires(mapQuestionnaires(rawQuestionnaires))
    } catch {
      if (currentRequestId !== requestIdRef.current) return
      setAllUnits([])
      setAllQuestionnaires([])
      showSnackbar('dashboard.storePerformance.error.loadData')
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setDataLoading(false)
      }
    }
  }, [showSnackbar, assignedCompany?.id, buildPeriodQueryParams])

  const refreshQuestionnaires = useCallback(async (companyId: string) => {
    if (!companyId) return
    const currentRequestId = ++requestIdRef.current
    try {
      const response = await questionarieServise(httpFetch).getQuestionnairesWithVersions({
        company_id: companyId,
        ...buildPeriodQueryParams(),
      })
      if (currentRequestId !== requestIdRef.current) return
      const rawQuestionnaires = Array.isArray(response) ? response : []
      setAllQuestionnaires(mapQuestionnaires(rawQuestionnaires))
    } catch {
      if (currentRequestId !== requestIdRef.current) return
      showSnackbar('dashboard.storePerformance.error.loadData')
    }
  }, [showSnackbar, buildPeriodQueryParams])

  useEffect(() => {
    if (hasAssignedCompany) {
      loadHierarchyData()
    }
  }, [hasAssignedCompany, loadHierarchyData])

  useEffect(() => {
    if (hasAssignedCompany) return
    if (selectedCompany?.value) {
      // Limpiar datos previos inmediatamente para evitar estados intermedios
      // inconsistentes cuando se cambia directamente de una compañía a otra.
      setAllUnits([])
      setAllQuestionnaires([])
      fetchData(String(selectedCompany.value))
    } else {
      setAllUnits([])
      setAllQuestionnaires([])
    }
  }, [hasAssignedCompany, selectedCompany?.value, fetchData])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    const companyId = hasAssignedCompany
      ? String(assignedCompany?.id ?? '')
      : String(selectedCompany?.value ?? '')
    refreshQuestionnaires(companyId)
  }, [
    periodParams?.appliedPeriodFrom,
    periodParams?.appliedPeriodTo,
    periodParams?.appliedPeriodYear,
    periodParams?.appliedPeriodWeek,
  ])

  return {
    assignedCompany,
    hasAssignedCompany,
    initialCompany,
    selectedCompany,
    setSelectedCompany,
    companiesList,
    companiesLoading,
    allUnits,
    allQuestionnaires,
    dataLoading,
    showSnackbar,
  }
}

export { useCompanyData }
